// Import rejestru akcji z pliku (SR-014): rozpoznanie typu, odczyt CSV (UTF-8 albo Windows-1250
// z polskiego Excela) lub .xlsx przez backend, a potem ten sam parser co przy wklejaniu.
import type { SpreadsheetTable } from "../../../api/reportsApi";
import {
  Category, ImportIssue, ParsedImport, REPORT_ACTION_RULES, ValidationRules, detectCsvDelimiter, parseDelimited, rowsFromTable,
} from "./actionLogFormat";

export const MAX_FILE_BYTES = 5_000_000;

const fail = (message: string): ParsedImport => ({ rows: [], issues: [{ level: "error", message }] });

// Excel w polskiej wersji zapisuje „CSV (rozdzielany przecinkami)” w Windows-1250, a „CSV UTF-8” w UTF-8
export const decodeCsv = (bytes: ArrayBuffer): { text: string; issue?: ImportIssue } => {
  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes) };
  } catch {
    return {
      text: new TextDecoder("windows-1250").decode(bytes),
      issue: { level: "info", message: "Plik nie jest w UTF-8: odczytano go jako Windows-1250 (polski Excel). Sprawdź polskie znaki w podglądzie." },
    };
  }
};

const errorMessage = (e: any): string =>
  e?.response?.data?.Message ?? e?.response?.data?.message ??
  (e?.response?.status === 413 ? "Plik jest za duży dla serwera." : undefined) ??
  (e?.response ? `Serwer odrzucił plik (kod ${e.response.status}).` : "Nie udało się wysłać pliku na serwer. Sprawdź połączenie.");

export const importActionLogFile = async (
  file: File,
  categories: Category[],
  newKey: () => string,
  today: string,
  parseXlsx: (file: File) => Promise<SpreadsheetTable>,
  rules: ValidationRules = REPORT_ACTION_RULES
): Promise<ParsedImport> => {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  if (file.size === 0) return fail("Plik jest pusty.");
  if (file.size > MAX_FILE_BYTES) return fail(`Plik ma ${(file.size / 1_000_000).toFixed(1)} MB. Maksymalnie 5 MB.`);
  if (ext === "xls") return fail("Stary format Excela (.xls). Otwórz plik w Excelu i zapisz jako „Skoroszyt programu Excel (.xlsx)”.");
  if (ext === "numbers") return fail("Plik z Numbers. Wyeksportuj go jako Excel (.xlsx) albo CSV (Plik → Eksportuj do).");
  if (ext === "ods") return fail("Plik OpenDocument (.ods). Zapisz go jako .xlsx albo CSV.");

  if (ext === "csv" || ext === "txt") {
    const { text, issue } = decodeCsv(await file.arrayBuffer());
    const delimiter = detectCsvDelimiter(text);
    const parsed = rowsFromTable(parseDelimited(text, delimiter), categories, newKey, today, 1, rules);
    return { ...parsed, issues: [...(issue ? [issue] : []), ...parsed.issues] };
  }

  if (ext === "xlsx") {
    let table: SpreadsheetTable;
    try {
      table = await parseXlsx(file);
    } catch (e) {
      return fail(errorMessage(e));
    }
    const parsed = rowsFromTable(table.rows, categories, newKey, today, table.firstRow, rules);
    const sheetInfo: ImportIssue[] =
      table.sheetCount > 1 ? [{ level: "info", message: `Odczytano arkusz „${table.sheetName}” (plik ma ${table.sheetCount} arkusze).` }] : [];
    return { ...parsed, issues: [...sheetInfo, ...parsed.issues] };
  }

  return fail(`Nieobsługiwany typ pliku „.${ext}”. Wgraj plik Excel (.xlsx) albo CSV według wzoru.`);
};
